import { ComponentConfig } from '@measured/puck';
import {
  MarginProps,
  marginFields,
} from '../../../page-builder/puck/reusable-props/margin';
import { v4 as uuidv4 } from 'uuid';
import { Guarantee } from './guarantee';
import Button from '../../../components/common/button';
import { IAsset } from '../../interface/apiServiceInterface';
import {
  defaultImage,
  ImageSelector,
} from '../../../components/common/image-selector/ImageSelector';
import { AxiosInstance } from 'axios';
import CustomTooltip from '../../../components/common/tooltip';
import { CustomizeProps } from '../../interface/commonInterface';
import TextCustomInput from '../../puck/reusable-props/TextCustomInput';
import { ReduxSelectorProps } from '../../services/constants';
import showComponent, {
  showComponentProps,
} from '../../puck/reusable-props/showComponent';
import {
  getBorderProps,
  getcurrentDeviceType,
  getMarginStyles,
  getPaddingStyles,
  getQuillEditorText,
} from '../../utils/helper';
import {
  ImageProps,
  imageProps,
} from '../../../page-builder/puck/reusable-props/image-props';
import { paddingFields, PaddingProps } from '../../puck/reusable-props/padding';
import Border, {
  IBorderValueProps,
  borderProps,
} from '../../../page-builder/puck/reusable-props/Border';
import { backgroungFields } from '../../puck/reusable-props/backgroungFields';
import ImageBorder, {
  imageBorderProps,
  ImageBorderValueProps,
} from '../../puck/reusable-props/ImageBorder';

export type CustomerGuarnteesProps = {
  guarantees: GuaranteeData[];
  hideImage: boolean;
  Header: CustomizeProps;
  border: IBorderValueProps;
  imageBorder: ImageBorderValueProps;
  dividerLine: boolean;
  background: {
    color: string;
    isInherit: boolean;
  };
} & MarginProps &
  ImageProps &
  showComponentProps &
  PaddingProps;

export interface GuaranteeData {
  id: string;
  image: IAsset;
  imageBorder: ImageBorderValueProps;
  name: string;
  hideImage: boolean;
  description: string;
  currentDeviceType: string;
  maxWidth: {
    [key: string]: string;
    mobile: string;
    tablet: string;
    desktop: string;
  };
  height: {
    [key: string]: string;
    mobile: string;
    tablet: string;
    desktop: string;
  };
}

export interface CustomerGuaranteesProps extends ReduxSelectorProps {
  baseService: AxiosInstance;
}

export const CustomerGuarantees = ({
  baseService,
  useAppSelector,
}: CustomerGuaranteesProps): ComponentConfig<CustomerGuarnteesProps> => {
  return {
    label: (
      <CustomTooltip title="CustomerGuarantees">
        <span>Customer Guarantees</span>
      </CustomTooltip>
    ) as React.ReactNode as string,
    fields: {
      ...showComponent,
      ...marginFields,
      ...paddingFields,
      ...backgroungFields,
      border: {
        type: 'custom',
        render: ({ value, onChange }) => (
          <Border value={value} onChange={onChange} title="Border" />
        ),
      },
      imageBorder: {
        type: 'custom',
        render: ({ value, onChange }) => (
          <ImageBorder value={value} onChange={onChange} title="Image Border" />
        ),
      },
      guarantees: {
        type: 'custom',
        label: 'Guarantee',
        render: ({ value, onChange }) => {
          value = value || [];

          const handleAddGuarantee = () => {
            const newGuarantee: GuaranteeData = {
              id: uuidv4(),
              image: defaultImage,
              currentDeviceType: 'desktop',
              imageBorder: {
                borderBottomLeft: {
                  desktop: '0',
                  tablet: '0',
                  mobile: '0',
                },
                borderBottomRight: {
                  desktop: '0',
                  tablet: '0',
                  mobile: '0',
                },
                borderTopLeft: {
                  desktop: '0',
                  tablet: '0',
                  mobile: '0',
                },
                borderTopRight: {
                  desktop: '0',
                  tablet: '0',
                  mobile: '0',
                },
                deviceType: 'desktop',
              },
              hideImage: false,
              name: '',
              description: '',
              maxWidth: {
                desktop: '100',
                mobile: '100',
                tablet: '100',
              },
              height: {
                desktop: '100',
                mobile: '100',
                tablet: '100',
              },
            };
            onChange([newGuarantee, ...value]);
          };

          const handleEditGuarantee = (
            index: number,
            key: keyof GuaranteeData,
            newValue: IAsset | string,
          ) => {
            onChange(
              value.map((guarantee: GuaranteeData, i: number) =>
                i === index ? { ...guarantee, [key]: newValue } : guarantee,
              ),
            );
          };

          const handleRemoveGuarantee = (index: number) => {
            onChange(value.filter((_: unknown, i: number) => i !== index));
          };
          return (
            <div>
              <Button
                label="+ Add new Guarantee"
                className="mt-4 w-full"
                variant="phoenix"
                onClick={handleAddGuarantee}
              />
              {value.map((guarantee: GuaranteeData, index: number) => (
                <div key={guarantee.id} className="mt-4">
                  <ImageSelector
                    handleSubmit={selectedImage => {
                      handleEditGuarantee(index, 'image', selectedImage);
                    }}
                    selectedImage={guarantee.image}
                    baseService={baseService}
                  />
                  <input
                    className="mt-5 bg-white border border-gray-300 rounded px-4 py-3 text-sm w-full transition-colors duration-50 ease-in pagebuilder-theme"
                    placeholder="Name"
                    value={guarantee?.name}
                    onChange={e =>
                      handleEditGuarantee(index, 'name', e.target.value)
                    }
                  />
                  <textarea
                    className="mt-5 bg-white border border-gray-300 rounded px-4 py-3 text-sm w-full transition-colors duration-50 ease-in pagebuilder-theme"
                    placeholder="Description"
                    value={guarantee.description}
                    onChange={e =>
                      handleEditGuarantee(index, 'description', e.target.value)
                    }
                  />
                  <Button
                    label="- Remove Guarantee"
                    className="mt-4 w-full"
                    variant="phoenix_secondary"
                    onClick={() => handleRemoveGuarantee(index)}
                  />
                </div>
              ))}
            </div>
          );
        },
      },
      Header: {
        type: 'custom',
        label: 'Header',
        render: ({ value, onChange, field }) => (
          <TextCustomInput
            showComponent={true}
            value={value}
            onChange={onChange}
            title={field?.label}
          />
        ),
      },
      ...imageProps([]),
      hideImage: {
        type: 'radio',
        label: 'Hide Image',
        options: [
          { value: true, label: 'True' },
          { value: false, label: 'False' },
        ],
      },
      dividerLine: {
        type: 'radio',
        label: 'Divider Line',
        options: [
          { value: true, label: 'Yes' },
          { value: false, label: 'No' },
        ],
      },
    },
    defaultProps: {
      showComponent: ['desktop', 'tablet', 'mobile'],
      padding: {
        deviceType: 'desktop',
        paddingBottom: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        paddingLeft: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        paddingRight: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        paddingTop: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
      },
      image: {
        height: {
          desktop: '100',
          mobile: '100',
          tablet: '100',
        },
        maxWidth: {
          desktop: '100',
          mobile: '100',
          tablet: '100',
        },
        deviceType: 'desktop',
      },
      dividerLine: true,
      Header: {
        weight: {
          mobile: 'font-normal',
          tablet: 'font-normal',
          desktop: 'font-normal',
        },
        showComponent: {
          mobile: true,
          tablet: true,
          desktop: true,
        },
        deviceType: 'desktop',
        text: 'Guarantees',
        textColor: {
          mobile: '#000000',
          tablet: '#000000',
          desktop: '#000000',
        },
        fontSize: {
          mobile: 'text-sm',
          tablet: 'text-sm',
          desktop: 'text-sm',
        },
      },
      margin: {
        desktop: {
          top: '0',
          bottom: '0',
          right: '0',
          left: '0',
        },
        tablet: {
          top: '0',
          bottom: '0',
          right: '0',
          left: '0',
        },
        mobile: {
          top: '0',
          bottom: '0',
          right: '0',
          left: '0',
        },
      },
      guarantees: [],
      ...borderProps,
      ...imageBorderProps,
      hideImage: false,
      background: {
        color: '#ffffff',
        isInherit: false,
      },
    },
    render: ({
      Header,
      image,
      margin,
      guarantees,
      showComponent,
      dividerLine,
      border,
      hideImage,
      padding,
      imageBorder,
      background,
      editMode,
    }) => {
      const windowWidth = useAppSelector(state => state.store.currentWidth);
      const currentDeviceType = getcurrentDeviceType(windowWidth, editMode);
      const marginStyles = getMarginStyles(margin, currentDeviceType);

      const state = useAppSelector(state => state);
      const newHeader = {
        ...Header,
        text: getQuillEditorText(Header?.text, state),
      };

      return (
        <>
          {showComponent?.includes(currentDeviceType) && (
            <div
              style={{
                backgroundColor: background?.isInherit
                  ? 'inherit'
                  : background?.color,
                ...getBorderProps(border, currentDeviceType),
                ...marginStyles,
                ...getPaddingStyles(padding, currentDeviceType),
              }}
              className={`pt-10 pb-5 ${border?.borderStyle?.[currentDeviceType]} `}>
              <div className="pb-5 border-b border-gray-200">
                {newHeader?.showComponent?.[currentDeviceType] && (
                  <div className="flex items-center justify-center">
                    {dividerLine && (
                      <div className="flex-grow border-b border-black mx-2" />
                    )}
                    <p
                      id="contact-info-heading"
                      className={`${newHeader.weight?.[currentDeviceType]} ${newHeader.fontSize?.[currentDeviceType]} text-center`}
                      style={{
                        color: newHeader?.textColor?.[currentDeviceType],
                      }}>
                      {newHeader?.text}
                    </p>
                    {dividerLine && (
                      <div className="flex-grow border-b border-black mx-2" />
                    )}
                  </div>
                )}
                {guarantees && guarantees.length === 0 && (
                  <div className="mt-10 text-center text-lg text-gray-600">
                    No Guarantees yet
                  </div>
                )}

                {guarantees?.map(data => {
                  return (
                    <Guarantee
                      currentDeviceType={currentDeviceType}
                      key={data.id}
                      id={data.id}
                      height={image?.height}
                      imageBorder={imageBorder}
                      maxWidth={image?.maxWidth}
                      image={data?.image}
                      name={data.name}
                      description={data.description}
                      hideImage={hideImage}
                    />
                  );
                })}
              </div>
            </div>
          )}
        </>
      );
    },
  };
};
